import { useMemo, useState, type ReactNode } from "react";
import { BarChart3, Building2, Landmark, Table2 } from "lucide-react";
import type {
  LandDataDistrict,
  LandDataView,
  LandRegionCitySeries,
  LandRegionDataDistrict,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { useEvidence, type EvidenceItem } from "./evidence";
import { MetricCell, MetricCompareSection, mainYear, type MetricKey } from "./metrics";
import { lastKey, seriesText, signedPct, srcEvidence, ValueButton } from "./regionshared";
import { fmtNum, SectionCard } from "./shared";
import { CityYearlyTable, DistrictSectorDetail, DistrictTrendTable, SectorYearTable } from "./yearly";

/* -------------------------------------------------------------------------- */
/* 城市概况 + 区县分主题指标 — 数据快照(region_data), 与区域研判同一套数          */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* 城市概况                                                                    */
/* -------------------------------------------------------------------------- */

function CityTile({
  label,
  value,
  unit,
  sub,
  onOpen,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: ReactNode;
  onOpen?: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className="rounded-lg border border-cardline bg-canvas px-4 py-3 text-left enabled:hover:border-brand-200 enabled:hover:bg-brand-50"
      title={onOpen ? "查看来源" : undefined}
    >
      <div className="text-[12px] text-zinc-500">{label}</div>
      <div className="mt-1 text-[20px] font-semibold leading-tight tabular-nums text-zinc-900">
        {value}
        {unit ? <span className="ml-1 text-[12px] font-normal text-zinc-500">{unit}</span> : null}
      </div>
      {sub ? <div className="mt-0.5 text-[11px] leading-4 text-zinc-500">{sub}</div> : null}
    </button>
  );
}

/** 跨 2023→2024(第五次经济普查口径切换)算的名义 GDP 增速要提示不可比 */
const CENSUS_BREAK_NOTE = "2024 年起 GDP 为第五次全国经济普查后的口径，和 2023 年及以前不能直接比，跨这两年算的名义增速会受普查修订影响；看真实增长请用「GDP 实际增速」。";
const crossesCensusBreak = (span?: string) => {
  const [from, to, ...rest] = (span ?? "").match(/\d{4}/g)?.map(Number) ?? [];
  return from != null && to != null && !rest.length && from <= 2023 && to >= 2024;
};

const SIZE_LABELS: Array<[string, string]> = [
  ["large", "大型"],
  ["medium", "中型"],
  ["small_micro", "小微型"],
  ["small", "小型"],
  ["micro", "微型"],
  ["sme", "中小企业合计"],
];

export function CitySection({ data }: { data: LandDataView }): JSX.Element | null {
  const evidence = useEvidence();
  const rd = data.region_data;
  const cp = rd?.city_profile;
  if (!rd || !cp) return null;
  const src = cp.sources ?? {};
  const city = rd.city || data.project.city || "城市";
  const tiles: JSX.Element[] = [];

  const gdpYear = lastKey(cp.gdp?.series);
  const realYear = lastKey(cp.gdp?.real_growth);
  if (gdpYear && cp.gdp) {
    const v = cp.gdp.series[gdpYear];
    const real = realYear ? cp.gdp.real_growth?.[realYear] : undefined;
    tiles.push(
      <CityTile
        key="gdp"
        label={`GDP（${gdpYear}年）`}
        value={fmtNum(v, 0)}
        unit="亿元"
        sub={
          [real != null ? `${realYear}年实际增长 ${real.toFixed(1)}%` : "", cp.gdp.span ? `${cp.gdp.span} 名义累计 ${signedPct(cp.gdp.growth_pct)}` : ""]
            .filter(Boolean)
            .join(" · ") || undefined
        }
        onOpen={() =>
          evidence.open(
            srcEvidence({ title: `${city} · GDP`, value: fmtNum(v, 2), unit: "亿元", rows: src.gdp, basis: [seriesText(cp.gdp?.series, 0)] }),
          )
        }
      />,
    );
  }
  const rank = cp.gdp_national_rank;
  if (rank?.latest != null) {
    tiles.push(
      <CityTile
        key="rank"
        label="GDP 全国排名"
        value={`第 ${fmtNum(rank.latest, 0)}`}
        sub={Object.keys(rank.series).sort().map((y) => `${y.slice(2)}年第${rank.series[y]}`).join(" · ")}
        onOpen={() =>
          evidence.open(srcEvidence({ title: `${city} · GDP 全国排名`, value: `第 ${fmtNum(rank.latest, 0)}`, rows: src.gdp_national_rank }))
        }
      />,
    );
  }
  const series = (key: string, label: string, s: LandRegionCitySeries | null | undefined, digits: number): void => {
    if (!s || s.latest == null) return;
    tiles.push(
      <CityTile
        key={key}
        label={`${label}${s.latest_year ? `（${s.latest_year}年）` : ""}`}
        value={fmtNum(s.latest, digits)}
        unit={s.unit}
        sub={s.span && s.growth_pct != null ? `${s.span} 累计 ${signedPct(s.growth_pct)}` : undefined}
        onOpen={() =>
          evidence.open(
            srcEvidence({
              title: `${city} · ${label}`,
              value: fmtNum(s.latest, digits),
              unit: s.unit,
              rows: src[key],
              basis: [seriesText(s.series, digits)],
            }),
          )
        }
      />,
    );
  };
  if (cp.density != null) {
    tiles.push(
      <CityTile
        key="density"
        label={`经济密度（${cp.density_year ?? ""}年）`}
        value={fmtNum(cp.density, 2)}
        unit="亿元/km²"
        sub={`GDP ÷ 行政区域面积 ${fmtNum(cp.area_km2, 0)} km²`}
        onOpen={() =>
          evidence.open(
            srcEvidence({
              title: `${city} · 经济密度`,
              value: fmtNum(cp.density, 2),
              unit: "亿元/km²",
              rows: src.area_km2,
              formula: `${cp.density_year}年 GDP ${fmtNum(gdpYear ? cp.gdp?.series[gdpYear] : null, 0)} 亿元 ÷ 面积 ${fmtNum(cp.area_km2, 2)} km²`,
            }),
          )
        }
      />,
    );
  }
  series("population", "常住人口", cp.population, 1);
  if (cp.universities?.latest != null) {
    const uniRank = cp.universities_national_rank?.latest;
    tiles.push(
      <CityTile
        key="uni"
        label="高校数量"
        value={fmtNum(cp.universities.latest, 0)}
        unit="所"
        sub={uniRank != null ? `全国第 ${fmtNum(uniRank, 0)}` : undefined}
        onOpen={() =>
          evidence.open(
            srcEvidence({
              title: `${city} · 高校数量`,
              value: fmtNum(cp.universities?.latest, 0),
              unit: "所",
              rows: [...(src.universities ?? []), ...(src.universities_national_rank ?? [])],
            }),
          )
        }
      />,
    );
  }
  series("industrial_output", "规上工业总产值", cp.industrial_output, 0);
  series("above_scale_firms", "规上工业企业", cp.above_scale_firms, 0);
  series("new_registrations", "新登记企业", cp.new_registrations, 0);

  const size = cp.size_structure ?? {};
  const sizeCols = SIZE_LABELS.filter(([k]) => size[k as keyof typeof size]);
  const sizeYears = Array.from(
    new Set(sizeCols.flatMap(([k]) => Object.keys(size[k as keyof typeof size]?.series ?? {}))),
  ).sort();
  const sectors = cp.sectors ?? [];
  const census = cp.census;

  if (!tiles.length && !sizeYears.length && !sectors.length && !census) return null;
  return (
    <SectionCard
      icon={<Building2 className="h-4 w-4" />}
      id="sec-city"
      title="城市概况"
      count={city}
      hint="城市的体量、增长和产业底子，点开数值可看来源"
    >
      {tiles.length ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{tiles}</div> : null}

      <CityYearlyTable rd={rd} city={city} />

      {sizeYears.length ? (
        <div className="mt-5">
          <div className="mb-2 text-[13px] font-medium text-zinc-700">规上工业企业规模结构（家）</div>
          <div className="overflow-x-auto rounded-lg border border-cardline">
            <table className="w-full min-w-[420px] text-[13px]">
              <thead>
                <tr className="bg-cardhead text-[12px] text-zinc-500">
                  <th className="px-3 py-2 text-left font-medium">年份</th>
                  {sizeCols.map(([k, label]) => (
                    <th key={k} className="px-3 py-2 text-right font-medium">
                      {label}
                    </th>
                  ))}
                  {cp.sme ? <th className="px-3 py-2 text-right font-medium">中小企业合计</th> : null}
                </tr>
              </thead>
              <tbody>
                {sizeYears.map((y, i) => (
                  <tr key={y} className={cn("border-t border-cardline/70", i % 2 ? "bg-canvas/60" : "bg-white")}>
                    <td className="px-3 py-1.5 text-zinc-700">{y}</td>
                    {sizeCols.map(([k, label]) => {
                      const s = size[k as keyof typeof size];
                      const v = s?.series[y];
                      return (
                        <td key={k} className="px-2 py-1">
                          <ValueButton
                            value={fmtNum(v, 0)}
                            onOpen={
                              v == null
                                ? undefined
                                : () =>
                                    evidence.open(
                                      srcEvidence({
                                        title: `${city} · ${y}年 规上工业${label}企业`,
                                        value: fmtNum(v, 0),
                                        unit: "家",
                                        rows: (src[`enterprise_size_${k}`] ?? []).filter((r) => String(r.year) === y),
                                      }),
                                    )
                            }
                          />
                        </td>
                      );
                    })}
                    {cp.sme ? (
                      <td className="px-3 py-1.5 text-right tabular-nums text-zinc-700">{fmtNum(cp.sme.total[y], 0)}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cp.sme ? (
            <p className="mt-1.5 text-[12px] text-zinc-500">
              中小企业（中型 + 小微型）{cp.sme.first_year}→{cp.sme.last_year} 累计 {signedPct(cp.sme.change_pct)}，是产业园的客户基础。
            </p>
          ) : null}
        </div>
      ) : null}

      {sectors.length ? (
        <div className="mt-5">
          <div className="mb-2 text-[13px] font-medium text-zinc-700">
            规上工业分行业{cp.sectors_growth_only ? "增速" : "产值（亿元）"}
          </div>
          <SectorYearTable
            items={sectors}
            growthOnly={!!cp.sectors_growth_only}
            title={city}
            sources={src.sector_output ?? src.sector_growth}
            showRank
          />
        </div>
      ) : null}

      {census ? (
        <div className="mt-5 rounded-lg border border-cardline px-4 py-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[13px] font-medium text-zinc-700">第五次经济普查（{census.year}年，全部工业企业，含规模以下）</span>
            <button
              type="button"
              className="text-[12px] text-brand-700 hover:underline"
              onClick={() =>
                evidence.open(
                  srcEvidence({
                    title: `${city} · 第五次经济普查 工业企业`,
                    value: fmtNum(census.total_enterprises, 0),
                    unit: "家",
                    rows: [
                      {
                        year: census.year,
                        sub: "",
                        value: census.total_revenue,
                        unit: "亿元",
                        caliber: "工业企业法人单位营业收入（全部工业企业）",
                        title: census.source_title,
                        url: census.source_url,
                        quote: "",
                      },
                    ],
                    basis: ["重化工按研判口径：石化、化工、钢铁、有色、非金属矿、造纸等，汽车不剥离"],
                  }),
                )
              }
            >
              查看来源
            </button>
          </div>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-[11px] text-zinc-400">工业企业 / 营业收入</div>
              <div className="text-[14px] font-medium tabular-nums text-zinc-900">
                {fmtNum(census.total_enterprises, 0)} 家 · {fmtNum(census.total_revenue, 0)} 亿元
              </div>
            </div>
            <div>
              <div className="text-[11px] text-zinc-400">重化工营收占比</div>
              <div className="text-[14px] font-medium tabular-nums text-zinc-900">
                {fmtNum(census.heavy_share_pct, 1)}%（{fmtNum(census.heavy_revenue, 0)} 亿元）
              </div>
            </div>
            <div>
              <div className="text-[11px] text-zinc-400">剥离后营收</div>
              <div className="text-[14px] font-medium tabular-nums text-zinc-900">{fmtNum(census.revenue_after_strip, 0)} 亿元</div>
            </div>
            <div>
              <div className="text-[11px] text-zinc-400">剥离后企业</div>
              <div className="text-[14px] font-medium tabular-nums text-zinc-900">
                {census.enterprises_after_strip != null ? `${fmtNum(census.enterprises_after_strip, 0)} 家` : "—"}
              </div>
            </div>
          </div>
          <dl className="mt-3 space-y-1.5 text-[12px] leading-5 text-zinc-600">
            <div>
              <dt className="inline text-zinc-400">剥离的重化工：</dt>
              <dd className="inline">{census.heavy.map((x) => `${x.sector} ${fmtNum(x.revenue, 0)}亿`).join("、") || "无"}</dd>
            </div>
            <div>
              <dt className="inline text-zinc-400">剥离后营收前列：</dt>
              <dd className="inline">
                {census.top_by_revenue.map((x) => `${x.sector} ${fmtNum(x.revenue, 0)}亿（${fmtNum(x.share_pct, 1)}%）`).join("、")}
              </dd>
            </div>
            {census.top_by_enterprises.length ? (
              <div>
                <dt className="inline text-zinc-400">企业数最多（中小企业多，产业园潜在客户）：</dt>
                <dd className="inline">{census.top_by_enterprises.map((x) => `${x.sector} ${fmtNum(x.enterprises, 0)}家`).join("、")}</dd>
              </div>
            ) : null}
          </dl>
          {census.partial ? (
            <p className="mt-2 text-[11px] text-zinc-400">
              公报只列了 {census.listed_industries} 个行业，没列出的行业里可能还有重化工，重化工占比可能偏低。
            </p>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* 区县指标对比（分主题页签）                                                     */
/* -------------------------------------------------------------------------- */

type TabKey = "trend" | "economy" | "industry" | "newreg" | "people" | "electricity" | "land" | "sectors";

interface Row {
  name: string;
  isTarget: boolean;
  d: LandRegionDataDistrict;
  base?: LandDataDistrict;
}

interface Col {
  key: string;
  label: string;
  /** 用于排序与条形图; 非数值列返回 null */
  value?: (r: Row) => number | null;
  render: (r: Row) => JSX.Element;
  barUnit?: string;
  barDigits?: number;
  minWidth?: string;
}

const TABS: Array<{ key: TabKey; label: string; note: string }> = [
  { key: "trend", label: "逐年趋势", note: "" },
  {
    key: "economy",
    label: "经济",
    note: "经济密度 = GDP ÷ 行政区域面积；名义年均增速按首末年 GDP 复合计算；实际增速为官方可比价增速，近三年均值比此前三年变动 1.5 个百分点以上记为加快或放缓。",
  },
  { key: "industry", label: "工业", note: "规上工业为规模以上工业企业口径；官方只发布营收的区以营收代替，已标注。" },
  {
    key: "newreg",
    label: "新登记",
    note: "各区公布口径不同：有的是新登记企业，有的是含个体户的市场主体，不同口径之间只比增速、不比数量；年末存量不算新登记。",
  },
  { key: "people", label: "人口与人才", note: "人均 GDP = GDP ÷ 常住人口；区内高校按教育部阳光高考平台登记的校区地址归区，一校多个校区的各区都算。" },
  { key: "electricity", label: "用电", note: "优先用工业用电量；区里不公布工业用电的用全社会用电量代替，已标注口径，两种口径只比走势。" },
  {
    key: "land",
    label: "土地",
    note: "国信网招拍挂成交逐宗明细（工业、仓储用地）按区逐年汇总：每年格内为成交面积（亩），下方为成交宗数和均价（万元/亩，= 成交总价 ÷ 成交面积）；带 * 的年份明细没取全，偏低。切到「条形」可按任一年份比较各区。",
  },
  { key: "sectors", label: "分行业", note: "重化工指石化、化工、钢铁、有色、非金属矿、造纸、电力热力燃气水等；只有增速的区标「仅增速」。" },
];

function BarRanking({
  rows,
  col,
  onPick,
}: {
  rows: Row[];
  col: Col;
  onPick: (r: Row) => void;
}): JSX.Element {
  const val = (r: Row): number | null => col.value?.(r) ?? null;
  const withValue = rows.filter((r) => val(r) != null).sort((a, b) => (val(b) as number) - (val(a) as number));
  const missing = rows.filter((r) => val(r) == null);
  const max = Math.max(1e-9, ...withValue.map((r) => Math.abs(val(r) as number)));
  return (
    <div className="max-w-3xl space-y-1">
      {withValue.map((r) => {
        const v = val(r) as number;
        const pct = (Math.abs(v) / max) * 100;
        return (
          <button
            key={r.name}
            type="button"
            onClick={() => onPick(r)}
            className="grid w-full grid-cols-[96px_minmax(0,1fr)] items-center gap-2 rounded px-1 py-0.5 text-left text-[12px] hover:bg-brand-50 sm:grid-cols-[128px_minmax(0,1fr)]"
          >
            <span className={cn("truncate", r.isTarget ? "font-semibold text-brand-900" : "text-zinc-600")}>
              {r.isTarget ? "★ " : ""}
              {r.name}
            </span>
            <span className="flex items-center gap-2">
              <span
                className={cn("block h-3 shrink-0 rounded-sm", r.isTarget ? "bg-brand-600" : v < 0 ? "bg-zinc-300" : "bg-brand-200")}
                style={{ width: `calc((100% - 80px) * ${Math.max(pct, 1.5) / 100})` }}
              />
              <span className="shrink-0 tabular-nums text-zinc-800">
                {col.barUnit === "%" ? signedPct(v) : fmtNum(v, col.barDigits ?? 1)}
              </span>
            </span>
          </button>
        );
      })}
      {missing.map((r) => (
        <div
          key={r.name}
          className="grid w-full grid-cols-[96px_minmax(0,1fr)] items-center gap-2 px-1 py-0.5 text-[12px] sm:grid-cols-[128px_minmax(0,1fr)]"
        >
          <span className="truncate text-zinc-400">{r.name}</span>
          <span className="text-zinc-300">无数据</span>
        </div>
      ))}
    </div>
  );
}

export function DistrictDataSection({ data }: { data: LandDataView }): JSX.Element | null {
  const evidence = useEvidence();
  const rd = data.region_data;
  const [tab, setTab] = useState<TabKey>("trend");
  const [view, setView] = useState<"table" | "bars">("table");
  const [barKey, setBarKey] = useState<string>("");
  const [sort, setSort] = useState<{ key: string; desc: boolean } | null>(null);

  const rows: Row[] = useMemo(() => {
    if (!rd) return [];
    const baseByName = new Map(data.districts.map((b) => [b.name, b]));
    return rd.districts.map((d) => ({ name: d.district, isTarget: d.is_target, d, base: baseByName.get(d.district) }));
  }, [rd, data.districts]);

  const city = rd?.city || data.project.city || "区县";
  const colYears = useMemo(() => {
    const out = {} as Record<MetricKey, number | null>;
    for (const k of ["gdp", "industrial_output", "industrial_firms"] as MetricKey[]) out[k] = mainYear(data.districts, k);
    return out;
  }, [data.districts]);

  const cols: Col[] = useMemo(() => {
    const open = (item: EvidenceItem) => evidence.open(item);
    const baseCell = (r: Row, k: MetricKey): JSX.Element =>
      r.base ? <MetricCell row={r.base} metric={k} colYear={colYears[k]} scope={city} /> : <span className="text-zinc-300">—</span>;
    switch (tab) {
      case "economy":
        return [
          { key: "gdp", label: "GDP（亿元）", value: (r) => r.base?.gdp ?? r.d.economy.gdp, render: (r) => baseCell(r, "gdp"), barDigits: 0 },
          {
            key: "gdp_real_recent",
            label: "近三年实际增速",
            value: (r) => r.d.economy.gdp_real_recent_pct ?? null,
            barUnit: "%",
            render: (r) => (
              <ValueButton
                value={r.d.economy.gdp_real_recent_pct != null ? `${r.d.economy.gdp_real_recent_pct.toFixed(1)}%` : "—"}
                sub={r.d.economy.gdp_real_recent_span ? `${r.d.economy.gdp_real_recent_span} 平均` : undefined}
                onOpen={
                  r.d.economy.gdp_real_recent_pct == null
                    ? undefined
                    : () =>
                        open(
                          srcEvidence({
                            title: `${city} · ${r.name} · GDP 实际增速`,
                            value: `${r.d.economy.gdp_real_recent_pct?.toFixed(1)}%`,
                            rows: r.d.sources.gdp_growth,
                            formula: `${r.d.economy.gdp_real_recent_span} 三年实际增速的平均`,
                            basis: [seriesText(r.d.economy.gdp_real_growth, 1, 1, "%")],
                          }),
                        )
                }
              />
            ),
          },
          {
            key: "gdp_real_trend",
            label: "增速走势",
            value: (r) => r.d.economy.gdp_real_trend_pp ?? null,
            render: (r) => {
              const c = r.d.economy.gdp_real_trend_pp;
              const word = c == null ? "" : c >= 1.5 ? "加快" : c <= -1.5 ? "放缓" : "基本平稳";
              return (
                <ValueButton
                  value={
                    c == null ? (
                      "—"
                    ) : (
                      <span className={cn(word === "放缓" && "text-rose-600", word === "加快" && "text-emerald-700")}>{word}</span>
                    )
                  }
                  sub={c != null ? `比此前三年 ${c > 0 ? "+" : ""}${c.toFixed(1)} 个百分点` : undefined}
                />
              );
            },
          },
          {
            key: "gdp_cagr",
            label: "GDP 名义年均增速",
            value: (r) => r.d.economy.gdp_cagr_pct,
            barUnit: "%",
            render: (r) => (
              <ValueButton
                value={signedPct(r.d.economy.gdp_cagr_pct)}
                sub={r.d.economy.gdp_span || undefined}
                onOpen={() =>
                  open(
                    srcEvidence({
                      title: `${city} · ${r.name} · GDP 年均增速`,
                      value: signedPct(r.d.economy.gdp_cagr_pct),
                      rows: r.d.sources.gdp,
                      formula: `按 ${r.d.economy.gdp_span || "首末年"} 复合计算`,
                      basis: [
                        seriesText(r.d.economy.gdp_series, 0),
                        ...(crossesCensusBreak(r.d.economy.gdp_span) ? [CENSUS_BREAK_NOTE] : []),
                      ],
                    }),
                  )
                }
              />
            ),
          },
          {
            key: "area",
            label: "面积（km²）",
            value: (r) => r.d.economy.area_km2,
            barDigits: 0,
            render: (r) => (
              <ValueButton
                value={fmtNum(r.d.economy.area_km2, 1)}
                onOpen={() =>
                  open(srcEvidence({ title: `${city} · ${r.name} · 行政区域面积`, value: fmtNum(r.d.economy.area_km2, 2), unit: "km²", rows: r.d.sources.area }))
                }
              />
            ),
          },
          {
            key: "density",
            label: "经济密度（亿元/km²）",
            value: (r) => r.d.economy.density,
            barDigits: 1,
            render: (r) => (
              <ValueButton
                value={fmtNum(r.d.economy.density, 1)}
                onOpen={() =>
                  open(
                    srcEvidence({
                      title: `${city} · ${r.name} · 经济密度`,
                      value: fmtNum(r.d.economy.density, 2),
                      unit: "亿元/km²",
                      rows: [...(r.d.sources.gdp ?? []), ...(r.d.sources.area ?? [])],
                      formula: `GDP ${fmtNum(r.d.economy.gdp, 1)} 亿元 ÷ 面积 ${fmtNum(r.d.economy.area_km2, 1)} km²`,
                    }),
                  )
                }
              />
            ),
          },
        ];
      case "industry":
        return [
          {
            key: "output",
            label: "规上工业产值（亿元）",
            value: (r) => r.base?.industrial_output ?? r.d.industry.output,
            render: (r) => baseCell(r, "industrial_output"),
            barDigits: 0,
          },
          {
            key: "firms",
            label: "规上工业企业（家）",
            value: (r) => r.base?.industrial_firms ?? r.d.industry.firms,
            render: (r) => baseCell(r, "industrial_firms"),
            barDigits: 0,
          },
          {
            key: "output_change",
            label: "产值变化",
            value: (r) => r.d.industry.output_growth_pct,
            barUnit: "%",
            render: (r) => (
              <ValueButton value={signedPct(r.d.industry.output_growth_pct)} sub={r.d.industry.output_span || undefined} />
            ),
          },
          ...(rows.some((r) => r.d.industry.sme || r.d.industry.size_structure?.large)
            ? ([
                {
                  key: "size_large",
                  label: "大型企业（家）",
                  value: (r) => r.d.industry.size_structure?.large?.latest ?? null,
                  barDigits: 0,
                  render: (r) => {
                    const s = r.d.industry.size_structure?.large;
                    return (
                      <ValueButton
                        value={fmtNum(s?.latest, 0)}
                        sub={s?.latest_year ? `${s.latest_year}年` : undefined}
                        onOpen={
                          s?.latest == null
                            ? undefined
                            : () =>
                                open(
                                  srcEvidence({
                                    title: `${city} · ${r.name} · 规上工业大型企业`,
                                    value: fmtNum(s.latest, 0),
                                    unit: "家",
                                    rows: r.d.sources.size,
                                    basis: [seriesText(s.series, 0)],
                                  }),
                                )
                        }
                      />
                    );
                  },
                },
                {
                  key: "size_sme",
                  label: "中小企业（家）",
                  value: (r) => (r.d.industry.sme ? r.d.industry.sme.total[r.d.industry.sme.last_year] ?? null : null),
                  barDigits: 0,
                  render: (r) => {
                    const sme = r.d.industry.sme;
                    const v = sme ? sme.total[sme.last_year] : null;
                    return (
                      <ValueButton
                        value={fmtNum(v, 0)}
                        sub={sme ? `${sme.last_year}年 · 中型+小微型` : undefined}
                        onOpen={
                          !sme
                            ? undefined
                            : () =>
                                open(
                                  srcEvidence({
                                    title: `${city} · ${r.name} · 规上工业中小企业`,
                                    value: fmtNum(v, 0),
                                    unit: "家",
                                    rows: r.d.sources.size,
                                    basis: [seriesText(sme.total, 0)],
                                    formula: "中小企业 = 中型 + 小微型",
                                  }),
                                )
                        }
                      />
                    );
                  },
                },
                {
                  key: "size_sme_change",
                  label: "中小企业变化",
                  value: (r) => r.d.industry.sme?.change_pct ?? null,
                  barUnit: "%",
                  render: (r) => {
                    const sme = r.d.industry.sme;
                    return <ValueButton value={signedPct(sme?.change_pct)} sub={sme ? `${sme.first_year}→${sme.last_year}` : undefined} />;
                  },
                },
              ] as Col[])
            : []),
        ];
      case "newreg":
        return [
          {
            key: "nr_latest",
            label: "最新一年（户）",
            value: (r) => r.d.new_reg.latest,
            barDigits: 0,
            render: (r) => (
              <ValueButton
                value={fmtNum(r.d.new_reg.latest, 0)}
                sub={[r.d.new_reg.year ? `${r.d.new_reg.year}年` : "", r.d.new_reg.is_flow === false ? "非新增口径" : r.d.new_reg.class].filter(Boolean).join(" · ")}
                onOpen={
                  r.d.new_reg.latest == null
                    ? undefined
                    : () =>
                        open(
                          srcEvidence({
                            title: `${city} · ${r.name} · 新登记`,
                            value: fmtNum(r.d.new_reg.latest, 0),
                            unit: "户",
                            rows: r.d.sources.new_reg,
                            basis: [seriesText(r.d.new_reg.series, 0)],
                          }),
                        )
                }
              />
            ),
          },
          {
            key: "nr_cagr",
            label: "年均增速",
            value: (r) => r.d.new_reg.cagr_pct,
            barUnit: "%",
            render: (r) => <ValueButton value={signedPct(r.d.new_reg.cagr_pct)} sub={r.d.new_reg.span || undefined} />,
          },
          {
            key: "nr_series",
            label: "历年（万户）",
            minWidth: "16rem",
            render: (r) => (
              <ValueButton align="left" muted={!Object.keys(r.d.new_reg.series).length} value={seriesText(r.d.new_reg.series, 2, 10000) || "—"} />
            ),
          },
          {
            key: "nr_caliber",
            label: "口径",
            minWidth: "14rem",
            render: (r) => (
              <span className="block max-w-[18rem] truncate text-[12px] text-zinc-500" title={r.d.new_reg.caliber}>
                {r.d.new_reg.caliber || "—"}
              </span>
            ),
          },
        ];
      case "people":
        return [
          {
            key: "pop",
            label: "常住人口（万人）",
            value: (r) => r.d.people.population,
            barDigits: 1,
            render: (r) => (
              <ValueButton
                value={fmtNum(r.d.people.population, 1)}
                sub={r.d.people.population_year ? `${r.d.people.population_year}年` : undefined}
                onOpen={
                  r.d.people.population == null
                    ? undefined
                    : () =>
                        open(
                          srcEvidence({
                            title: `${city} · ${r.name} · 常住人口`,
                            value: fmtNum(r.d.people.population, 2),
                            unit: "万人",
                            rows: r.d.sources.population,
                          }),
                        )
                }
              />
            ),
          },
          {
            key: "pop_cagr",
            label: "近三年年均",
            value: (r) => r.d.people.pop_cagr_pct,
            barUnit: "%",
            render: (r) => <ValueButton value={signedPct(r.d.people.pop_cagr_pct)} sub={r.d.people.pop_span || undefined} />,
          },
          {
            key: "gdp_pc",
            label: "人均 GDP（万元）",
            value: (r) => r.d.people.gdp_per_capita,
            barDigits: 1,
            render: (r) => (
              <ValueButton
                value={fmtNum(r.d.people.gdp_per_capita, 1)}
                onOpen={
                  r.d.people.gdp_per_capita == null
                    ? undefined
                    : () =>
                        open(
                          srcEvidence({
                            title: `${city} · ${r.name} · 人均 GDP`,
                            value: fmtNum(r.d.people.gdp_per_capita, 2),
                            unit: "万元",
                            rows: [...(r.d.sources.gdp ?? []), ...(r.d.sources.population ?? [])],
                            formula: `GDP ${fmtNum(r.d.economy.gdp, 1)} 亿元 ÷ 常住人口 ${fmtNum(r.d.people.population, 2)} 万人`,
                          }),
                        )
                }
              />
            ),
          },
          {
            key: "uni",
            label: "区内高校（所）",
            value: (r) => r.d.people.universities,
            barDigits: 0,
            render: (r) => (
              <ValueButton
                value={fmtNum(r.d.people.universities, 0)}
                sub={
                  r.d.people.universities != null
                    ? [
                        r.d.people.universities_undergrad != null ? `本科${fmtNum(r.d.people.universities_undergrad, 0)}` : "",
                        r.d.people.universities_vocational != null ? `高职${fmtNum(r.d.people.universities_vocational, 0)}` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : undefined
                }
                onOpen={
                  r.d.people.universities == null
                    ? undefined
                    : () =>
                        open(
                          srcEvidence({
                            title: `${city} · ${r.name} · 区内高校`,
                            value: fmtNum(r.d.people.universities, 0),
                            unit: "所",
                            rows: (r.d.sources.universities ?? []).filter((x) => !x.sub),
                          }),
                        )
                }
              />
            ),
          },
        ];
      case "electricity":
        return [
          {
            key: "elec",
            label: "最新一年用电",
            value: (r) => r.d.electricity.latest,
            barDigits: 1,
            render: (r) => (
              <ValueButton
                value={r.d.electricity.latest != null ? `${fmtNum(r.d.electricity.latest, 2)}` : "—"}
                sub={[r.d.electricity.year ? `${r.d.electricity.year}年` : "", r.d.electricity.unit].filter(Boolean).join(" · ")}
                onOpen={
                  r.d.electricity.latest == null
                    ? undefined
                    : () =>
                        open(
                          srcEvidence({
                            title: `${city} · ${r.name} · 用电量`,
                            value: fmtNum(r.d.electricity.latest, 2),
                            unit: r.d.electricity.unit,
                            rows: r.d.sources.electricity,
                            basis: [seriesText(r.d.electricity.series, 2)],
                          }),
                        )
                }
              />
            ),
          },
          {
            key: "elec_kind",
            label: "口径",
            render: (r) => (
              <span className="text-[12px] text-zinc-600">
                {r.d.electricity.latest == null ? "—" : r.d.electricity.is_industrial ? "工业用电" : "全社会用电（代替）"}
              </span>
            ),
          },
          {
            key: "elec_cagr",
            label: "近三年年均",
            value: (r) => r.d.electricity.cagr_pct,
            barUnit: "%",
            render: (r) => <ValueButton value={signedPct(r.d.electricity.cagr_pct)} sub={r.d.electricity.span || undefined} />,
          },
          {
            key: "elec_series",
            label: "历年",
            minWidth: "18rem",
            render: (r) => (
              <ValueButton align="left" muted={!Object.keys(r.d.electricity.series).length} value={seriesText(r.d.electricity.series, 2) || "—"} />
            ),
          },
        ];
      case "land": {
        const landCell = (r: Row) => r.d.land;
        const latestYear = (r: Row): string | null => {
          const ys = Object.keys(landCell(r)?.by_year ?? {}).sort();
          return ys[ys.length - 1] ?? null;
        };
        const landOpen = (r: Row, label: string, value: string, unit?: string): void =>
          open(
            srcEvidence({
              title: `${city} · ${r.name} · ${label}`,
              value,
              unit,
              formula: "国信网招拍挂成交逐宗明细（工业、仓储用地）按出让年份汇总；地价 = 成交总价 ÷ 成交面积；不含协议出让和划拨",
            }),
          );
        // 土地按区逐年展示: 每年一列(面积为主, 下方宗数 · 均价), 再接五年合计 / 最新地价 / 小地块占比
        const metaYears = rd?.land_meta?.years ?? [];
        const landYears = (metaYears.length ? metaYears : Array.from(new Set(rows.flatMap((r) => Object.keys(landCell(r)?.by_year ?? {})))))
          .filter((y) => /^\d{4}$/.test(y))
          .sort()
          .slice(-5);
        const incompleteLand = new Set(rd?.land_meta?.incomplete_years ?? []);
        const yearCols: Col[] = landYears.map((y) => ({
          key: `land_${y}`,
          label: `${y}年${incompleteLand.has(y) ? "*" : ""}（亩）`,
          value: (r: Row) => {
            const x = landCell(r)?.by_year[y];
            return x ? x.area_mu : null;
          },
          barDigits: 0,
          render: (r: Row) => {
            const x = landCell(r)?.by_year[y];
            if (!x) return <ValueButton value={<span className="text-zinc-300">—</span>} />;
            if (!x.parcels && !x.area_mu) return <ValueButton muted value="0" sub="无成交" />;
            const price = x.avg_price_wan_per_mu;
            return (
              <ValueButton
                value={fmtNum(x.area_mu, 0)}
                sub={`${fmtNum(x.parcels, 0)} 宗${price != null ? ` · ${fmtNum(price, 0)} 万/亩` : ""}`}
                onOpen={() =>
                  landOpen(
                    r,
                    `${y}年工业用地成交`,
                    `${fmtNum(x.area_mu, 1)} 亩 · ${fmtNum(x.parcels, 0)} 宗${price != null ? ` · 均价 ${fmtNum(price, 1)} 万元/亩` : ""}`,
                  )
                }
              />
            );
          },
        }));
        return [
          {
            key: "land_area",
            label: "五年合计（亩）",
            value: (r) => landCell(r)?.area_mu_5y ?? null,
            barDigits: 0,
            render: (r) => {
              const v = landCell(r)?.area_mu_5y;
              const n = landCell(r)?.parcels_5y;
              return (
                <ValueButton
                  value={fmtNum(v, 0)}
                  sub={n != null ? `${fmtNum(n, 0)} 宗` : undefined}
                  onOpen={v == null ? undefined : () => landOpen(r, "五年工业用地成交面积", fmtNum(v, 1), "亩")}
                />
              );
            },
          },
          ...yearCols,
          {
            key: "land_price",
            label: "最新地价（万元/亩）",
            value: (r) => landCell(r)?.price_latest ?? null,
            barDigits: 1,
            render: (r) => {
              const v = landCell(r)?.price_latest;
              return (
                <ValueButton
                  value={fmtNum(v, 1)}
                  sub={landCell(r)?.price_year ? `${landCell(r)?.price_year}年` : undefined}
                  onOpen={v == null ? undefined : () => landOpen(r, "工业地价", fmtNum(v, 1), "万元/亩")}
                />
              );
            },
          },
          {
            key: "land_small",
            label: "最近一年 30 亩以下",
            value: (r) => {
              const y = latestYear(r);
              const v = y ? landCell(r)?.by_year[y]?.small_share : null;
              return v == null ? null : v * 100;
            },
            barUnit: "%",
            render: (r) => {
              const y = latestYear(r);
              const v = y ? landCell(r)?.by_year[y]?.small_share : null;
              return <ValueButton value={v == null ? "—" : `${(v * 100).toFixed(0)}%`} sub={y ? `${y}年` : undefined} />;
            },
          },
        ];
      }
      case "sectors":
      default: {
        const sectorText = (r: Row, exHeavy: boolean): string => {
          const items = r.d.sectors.items.filter((s) => !exHeavy || !s.heavy).slice(0, 3);
          if (!items.length) return "—";
          return items
            .map(
              (s) =>
                `${s.sector}${s.heavy ? "（重化工）" : ""} ` +
                (r.d.sectors.growth_only ? `增速${signedPct(s.latest)}` : `${fmtNum(s.latest, 0)}亿`) +
                (!r.d.sectors.growth_only && s.growth_pct != null ? `（${signedPct(s.growth_pct)}）` : ""),
            )
            .join("；");
        };
        const openSectors = (r: Row): void =>
          open(
            srcEvidence({
              title: `${city} · ${r.name} · 分行业${r.d.sectors.growth_only ? "增速" : "产值"}`,
              value: r.d.sectors.items[0]?.sector ?? "—",
              rows: r.d.sources.sectors,
              basis: [r.d.sectors.growth_only ? "官方只公布行业增速，没有产值" : "规上工业分行业总产值"],
            }),
          );
        return [
          {
            key: "sec_top",
            label: "主要行业（前三）",
            minWidth: "22rem",
            render: (r) => (
              <ValueButton
                align="left"
                value={<span className="whitespace-normal text-[12px] font-normal leading-5 text-zinc-700">{sectorText(r, false)}</span>}
                sub={r.d.sectors.growth_only ? "仅增速" : undefined}
                onOpen={r.d.sectors.items.length ? () => openSectors(r) : undefined}
              />
            ),
          },
          {
            key: "sec_ex",
            label: "剥离重化工后（前三）",
            minWidth: "22rem",
            render: (r) => (
              <ValueButton
                align="left"
                value={<span className="whitespace-normal text-[12px] font-normal leading-5 text-zinc-700">{sectorText(r, true)}</span>}
                onOpen={r.d.sectors.items.length ? () => openSectors(r) : undefined}
              />
            ),
          },
        ];
      }
    }
  }, [tab, city, colYears, evidence, rows, rd]);

  if (!rd || !rd.districts.length) return <MetricCompareSection data={data} kind="districts" />;

  const numericCols = cols.filter((c) => c.value);
  const barCol = numericCols.find((c) => c.key === barKey) ?? numericCols[0];
  const sortCol = cols.find((c) => c.key === sort?.key && c.value) ?? numericCols[0];
  const desc = sort?.desc ?? true;
  const sorted = [...rows].sort((a, b) => {
    if (a.isTarget !== b.isTarget) return a.isTarget ? -1 : 1; // 本区置顶
    const va = sortCol?.value?.(a) ?? null;
    const vb = sortCol?.value?.(b) ?? null;
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return desc ? vb - va : va - vb;
  });
  const tabMeta = TABS.find((t) => t.key === tab);
  const onHeader = (c: Col): void => {
    if (!c.value) return;
    setSort((s) => (s?.key === c.key ? { key: c.key, desc: !s.desc } : { key: c.key, desc: true }));
  };

  return (
    <SectionCard
      icon={<Landmark className="h-4 w-4" />}
      id="sec-districts"
      title="区县指标对比"
      count={`${rows.length} 个区`}
      actions={
        numericCols.length && tab !== "trend" && tab !== "sectors" ? (
          <div className="inline-flex rounded-lg border border-cardline bg-white p-0.5">
            {(
              [
                ["table", "表格", Table2],
                ["bars", "条形", BarChart3],
              ] as const
            ).map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[12px]",
                  view === key ? "bg-brand-50 font-medium text-brand-900" : "text-zinc-500 hover:text-brand-800",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="mb-3 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key);
              setSort(null);
              setBarKey("");
            }}
            className={cn(
              "rounded-full px-3 py-1 text-[13px] font-medium transition-colors",
              tab === t.key ? "bg-brand-900 text-white" : "bg-canvas text-zinc-600 hover:bg-brand-50 hover:text-brand-800",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "trend" ? (
        <DistrictTrendTable rows={rows} city={city} incompleteYears={rd.land_meta?.incomplete_years} />
      ) : tab === "sectors" ? (
        <DistrictSectorDetail rows={rows} city={city} />
      ) : view === "bars" && barCol ? (
        <>
          {numericCols.length > 1 ? (
            <div className="mb-3 inline-flex flex-wrap rounded-lg border border-cardline bg-canvas p-0.5">
              {numericCols.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setBarKey(c.key)}
                  className={cn(
                    "rounded-md px-3 py-1 text-[12px] font-medium transition-colors",
                    barCol.key === c.key ? "bg-white text-brand-900 shadow-sm" : "text-zinc-500 hover:text-brand-800",
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          ) : null}
          <BarRanking rows={rows} col={barCol} onPick={() => setView("table")} />
        </>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-cardline">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead>
              <tr className="bg-cardhead text-[12px] text-zinc-500">
                <th className="sticky left-0 z-10 bg-cardhead px-3 py-2 text-left font-medium">区县</th>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onHeader(c)}
                    className={cn(
                      "whitespace-nowrap px-3 py-2 font-medium",
                      c.value ? "cursor-pointer select-none text-right hover:text-brand-800" : "text-left",
                    )}
                    style={c.minWidth ? { minWidth: c.minWidth } : undefined}
                  >
                    {c.label}
                    {sortCol?.key === c.key ? (desc ? " ↓" : " ↑") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr
                  key={r.name}
                  className={cn("border-t border-cardline/70", r.isTarget ? "bg-brand-50" : i % 2 ? "bg-canvas/60" : "bg-white")}
                >
                  <td
                    className={cn(
                      "sticky left-0 z-10 whitespace-nowrap px-3 py-1.5 align-top",
                      r.isTarget ? "bg-brand-50 font-semibold text-brand-900" : i % 2 ? "bg-canvas text-zinc-700" : "bg-white text-zinc-700",
                    )}
                  >
                    {r.isTarget ? <span className="mr-1 text-brand-600">★</span> : null}
                    {r.name}
                  </td>
                  {cols.map((c) => (
                    <td key={c.key} className="px-2 py-1 align-top" style={c.minWidth ? { minWidth: c.minWidth } : undefined}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === "trend" ? null : (
        <p className="mt-2.5 text-[12px] leading-5 text-zinc-400">点开数值可看来源和原文。{tabMeta?.note ?? ""}</p>
      )}
    </SectionCard>
  );
}
